import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  Box,
  Dialog,
  DialogContent,
  DialogTitle as MuiDialogTitle,
  Typography,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { Button, useToast } from '@redshelf/redshelf-ui';

import { SCREEN_SIZE_CATEGORIES } from 'constants';
import {
  MobileAppValuesContext,
  UserAgentValuesContext,
  UserContentItemModifiersContext,
  ViewportValuesContext,
} from 'contexts';
import IconButton from 'components/IconButton';
import { Modal, UsageBar } from '..';

const useStyles = makeStyles((theme) => {
  return {
    copyContainer: {
      maxWidth: '300px',
      paddingTop: 0,
      fontFamily: theme.typography.fontFamily,
    },
    row: {
      padding: theme.spacing(0, 2),
    },
    errorRow: {
      margin: theme.spacing(0, 2),
    },
    usageBar: {
      marginTop: theme.spacing(2.875),
    },
    copyContent: {
      flex: '1 100%',
      marginTop: '16px',
      border: '1px solid #4D4D4D',
      backgroundColor: '#E5E5E5',
      borderRadius: '4px',
      padding: '8px',
      height: '116.92px',
      minWidth: 'auto',
      overflow: 'auto',
      '& legend': {
        background: 'white',
        padding: '0 4px',
        fontSize: '12px',
      },
      fontFamily: theme.typography.fontFamily,
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      margin: theme.spacing(2, 0, 0),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    button: {
      margin: '0px 8px 0',

      '&:last-child': {
        marginRight: 0,
      },
    },
    titleRoot: {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      padding: theme.spacing(0.33333333, 2), // 2px 16px
      minHeight: theme.spacing(6.75), // 48px + 6px === Close icon size by default + the container offset
    },
    title: {
      marginRight: theme.spacing(6), // 48px === Close icon size by default
      fontSize: '1.175rem',
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(0.33333333), // 2px
      right: theme.spacing(0.33333333),
      '&:hover': {
        background: theme.palette.shelfBlue['50L'],
      },
    },
    dialogPaper: {
      maxWidth: theme.spacing(37.5), // 300px
    },
    dialogContent: {
      padding: 0,
      overflowX: 'hidden',
    },
    drmMessage: {
      display: 'block',
      fontSize: theme.typography.pxToRem(12),
      lineHeight: theme.typography.pxToRem(18),
      margin: theme.spacing(1, 4),
      marginBottom: 0,
      marginLeft: theme.spacing(2),
    },
    errorContainer: {
      display: 'flex',
      marginTop: theme.spacing(1.4), // 16px-4.800px = 11.2px
      background: 'rgba(211, 47, 47, 0.08)',
      //errorMain is other colour in RSUI
      border: '2px solid #D32F2F',
      borderRadius: theme.spacing(0.5),
      padding: theme.spacing(1.25),
    },
    errorIcon: {
      marginRight: theme.spacing(1.20875),
      //errorMain is other colour in RSUI
      color: '#D32F2F',
    },
    errorMessage: {
      color: theme.palette.greyBlue[700],
      fontSize: theme.spacing(1.75),
      lineHeight: '21px',
    },
  };
});

const CopyContentModal = ({
  anchorPosition,
  availablePercent,
  canCopy = true,
  copiedText,
  currentPercent,
  newPercent,
  onClose = () => {},
  onCloseAll = () => {},
  remainingAllowance,
  setUpdatePosition,
  shouldShowAsDialog, // Allow override for testing
  ...props
}) => {
  const classes = useStyles();
  const title = 'Copy Content';

  const { enqueueToast } = useToast();
  const toastMessage = "You've successfully copied content to your clipboard";

  const dialogDrmMessage =
    'Clicking "Copy" will copy this text to your clipboard and contribute to your copy/paste limit.';
  const errorLabel = (
    <>
      Exceeds copy allowance by{' '}
      <span className="noTranslate">
        {parseFloat(newPercent * 100 - remainingAllowance * 100)?.toFixed(1)}%
      </span>
    </>
  );

  const usageBarAlert = {
    alertMessage: {
      isError: false,
      menu: 'copy',
    },
    alertErrorMessage: {
      isError: true,
      menu: 'copy',
    },
  };

  const [isErrorHidden, setIsErrorHidden] = useState(true);

  const { isMobileApp } = useContext(MobileAppValuesContext);
  const { userAgent } = useContext(UserAgentValuesContext);
  const { viewport } = useContext(ViewportValuesContext);
  const { onDialogClose, onDialogOpen } = useContext(
    UserContentItemModifiersContext,
  );

  shouldShowAsDialog =
    shouldShowAsDialog !== undefined // Allow override for testing
      ? shouldShowAsDialog
      : viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.TABLET;

  const handleClose = () => {
    onDialogClose('copyModal');
    onClose();
  };

  const handleClick = async () => {
    if (newPercent > remainingAllowance) {
      setIsErrorHidden(false);
      setUpdatePosition(true);
    } else {
      if (userAgent.isiOS && isMobileApp) {
        // The iOS mobile app does not support the ClipboardItem approach used for desktop Safari below.
        // But it does have the same restriction as desktop Safari in that the "await" will prevent the copy from working.
        // So we must use the navigator.clipboard.writeText approach but without waiting for the server response.
        // This opens a small window for circumventing copy DRM by blocking the connection and then choosing copy,
        // but any user sophisticated enough to do this can also figure out how to copy by other simpler means.
        try {
          navigator.clipboard.writeText(copiedText.snippet);
        } catch (e) {
          console.warn(e);
        }

        await copiedText.save();
      } else if (userAgent.isSafari) {
        // Safari does not allow copying to the clipboard without a user gesture,
        // which is invalidated by the call to await the network request.
        // So we must use a ClipboardItem object that accepts a promise.
        const snippetPromise = async () => {
          await copiedText.save();
          return copiedText.snippet;
        };

        try {
          await navigator.clipboard.write([
            new window.ClipboardItem({ 'text/plain': snippetPromise() }),
          ]);
        } catch (e) {
          console.warn(e);
        }
      } else {
        await copiedText.save();
        try {
          navigator.clipboard.writeText(copiedText.snippet);
        } catch (e) {
          console.warn(e);
        }
      }

      enqueueToast(toastMessage, {
        variant: 'success',
      });

      onDialogClose('copyModal');
      onCloseAll();
    }
  };

  const UsageBarContainer = () => {
    return (
      <Box className={`${classes.row} ${classes.usageBar}`}>
        <UsageBar
          alert={
            isErrorHidden
              ? usageBarAlert.alertMessage
              : usageBarAlert.alertErrorMessage
          }
          errorLabel={errorLabel}
          id="copy-modal-drm"
          usageText="Previously Copied"
          availablePercent={availablePercent}
          currentPercent={currentPercent}
          newPercent={newPercent}
          remainingAllowance={remainingAllowance}
        />
      </Box>
    );
  };

  const ButtonContainer = () => {
    return (
      <Box className={classes.buttonContainer}>
        <Button
          className={classes.button}
          onClick={handleClose}
          size="small"
          variant="secondary"
        >
          Cancel
        </Button>
        <Button className={classes.button} onClick={handleClick} size="small">
          Copy
        </Button>
      </Box>
    );
  };

  const CopiedTextField = () => {
    return (
      <Box className={classes.row}>
        <fieldset className={classes.copyContent}>
          <legend>Copied Content</legend>
          <span className="noTranslate">{copiedText.snippet}</span>
        </fieldset>
        <Typography variant="caption" className={classes.drmMessage}>
          {dialogDrmMessage}
        </Typography>
      </Box>
    );
  };

  const DialogTitle = () => {
    return (
      <MuiDialogTitle className={classes.titleRoot} disableTypography>
        <Typography
          id="copy-text-dialog-title"
          className={classes.title}
          variant="h2"
        >
          {title}
        </Typography>
        {onClose && (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        )}
      </MuiDialogTitle>
    );
  };

  const CopyTextModalBody = () => {
    return (
      <Box className={classes.copyContainer}>
        <CopiedTextField />
        <UsageBarContainer />
        <ButtonContainer />
      </Box>
    );
  };

  return (
    <>
      {shouldShowAsDialog ? (
        <Dialog
          aria-labelledby="copy-text-dialog-title"
          classes={{ paper: classes.dialogPaper }}
          onClose={(_, reason) => {
            if (reason !== 'backdropClick') {
              handleClose();
            }
          }}
          open={true}
          TransitionProps={{
            onEnter: () => onDialogOpen('copyModal'),
          }}
          disablePortal
          {...props}
        >
          <DialogTitle />
          <DialogContent className={classes.dialogContent} dividers>
            <CopyTextModalBody />
          </DialogContent>
        </Dialog>
      ) : (
        <Modal
          anchorPosition={anchorPosition}
          onClose={handleClose}
          title={title}
          TransitionProps={{
            onEnter: () => onDialogOpen('copyModal'),
          }}
          {...props}
        >
          <CopyTextModalBody />
        </Modal>
      )}
    </>
  );
};

CopyContentModal.propTypes = {
  anchorPosition: PropTypes.object,
  availablePercent: PropTypes.number.isRequired,
  canCopy: PropTypes.bool,
  copiedText: PropTypes.object.isRequired,
  currentPercent: PropTypes.number.isRequired,
  newPercent: PropTypes.number,
  onClose: PropTypes.func,
  onCloseAll: PropTypes.func,
  setUpdatePosition: PropTypes.func,
};

export default CopyContentModal;
